import { useAuth } from "@clerk/clerk-react";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { SlideAnimationContext } from "contexts/SlideAnimation";
import { useContext, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { OrganizationNiche, OrganizationVaProfile } from "schemas/dashboard";
import { Map } from "schemas/functions";
import { routes } from "schemas/routes";

import ConversationalTemplate from "features/Auth/Onboarding/ConversationalTemplate";
import {
  Template,
  TemplateContentType,
} from "features/Influencer/ContactList/schema";
import ReviewBentoAiTemplate from "features/Influencer/HomePage/CreateTemplateDialog/ReviewBentoAiTemplate";
import { fetcherAuth } from "utils/api";
import { makeDeepCopy } from "utils/updateLocalState";

import { SaveOrgVaProfileFunc } from "../../schema";

interface Props {
  organizationVaProfile: OrganizationVaProfile | null;
  saveOrgVaProfile: SaveOrgVaProfileFunc;
  handleBack: () => void;
  niche?: OrganizationNiche;
  handleNextStep: (niche?: OrganizationNiche) => void;
}

const BentoConvo = ({
  organizationVaProfile,
  saveOrgVaProfile,
  handleBack,
  niche,
  handleNextStep,
}: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const slideContext = useContext(SlideAnimationContext);
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setAlert, setErrorAlert } = useContext(AlertContext);
  const { fetchOutreachTemplates } = useContext(OutreachTemplatesContext);

  const [openReviewScreen, setOpenReviewScreen] = useState(false);
  const [editTemplate, setEditTemplate] = useState<Template>({
    subject: "",
    body: "",
    contentType: TemplateContentType.OUTREACH,
    name: "My Outreach",
  });
  const [followupTemplate, setFollowupTemplate] = useState<Template>({
    subject: "",
    body: "",
    contentType: TemplateContentType.FOLLOWUP,
    name: "My Follow-Up",
  });

  const handleSubmitForNiche = async (allTemplates: Template[]) => {
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/organization-niches/${niche?.id}/templates`,
        "PUT",
        {},
        {
          templateIds: allTemplates.map((t) => t.id),
        },
      );
      setAlert("Successfully saved templates for this niche", "success");
      const copy = makeDeepCopy(niche);
      copy.templates = allTemplates;
      handleNextStep(copy);
      await fetchOutreachTemplates();
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const handleNext = async (updatedForm?: Map) => {
    if (!updatedForm || !updatedForm.outreach) return;
    const allTemplates = [updatedForm.outreach];
    if (updatedForm.followup) {
      allTemplates.push(updatedForm.followup);
    }
    setEditTemplate(allTemplates[0]);
    if (allTemplates.length > 1) {
      setFollowupTemplate(allTemplates[1]);
    }
    setOpenReviewScreen(true);
  };

  const handleSubmitReviewedTemplate = async () => {
    const allTemplates = [editTemplate];
    if (followupTemplate?.body) {
      allTemplates.push(followupTemplate);
    }
    if (niche) {
      handleSubmitForNiche(allTemplates);
    } else {
      const copyVaProfile = makeDeepCopy(organizationVaProfile || {});
      copyVaProfile.fallbackTemplateIds = allTemplates.map(
        (t: Template) => t.id,
      );
      saveOrgVaProfile(copyVaProfile, false, false);
      if (slideContext) {
        await slideContext.slideOutUp();
        slideContext.slideInUp();
      }
      navigate(`/${routes.faqSelfServeOnboarding}${location.search}`);
    }
  };

  return (
    <>
      {openReviewScreen ? (
        <ReviewBentoAiTemplate
          editTemplate={editTemplate}
          setEditTemplate={setEditTemplate}
          handleBack={handleBack}
          handleNext={handleSubmitReviewedTemplate}
          followUpTemplate={followupTemplate}
          setFollowUpTemplate={setFollowupTemplate}
        />
      ) : (
        <ConversationalTemplate
          handleBack={handleBack}
          handleNext={handleNext}
        />
      )}{" "}
    </>
  );
};

export default BentoConvo;
